import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LuLock, LuTriangleAlert, LuCircleCheck, LuArrowLeft } from "react-icons/lu";
import XiLogo from "../components/ui/XiLogo";
import useTranslation from '../hooks/useTranslation';
import { deleteUserById } from '../components/api';

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const storedId = localStorage.getItem("neuro_user_id");
    const storedName = localStorage.getItem("neuro_user_name");
    setUserId(storedId);
    setUserName(storedName);
  }, []);

  const handleDelete = async () => {
    setLoading(true);
    setMessage('');

    try {
      let intUserId = userId;
      if (isNaN(Number(userId)) && userName) {
        const base = process.env.REACT_APP_API_URL || '/api';
        const res = await fetch(`${base}/user/details?user_name=${encodeURIComponent(userName)}`);
        const data = await res.json();
        if (data && data.data && data.data.user_id) {
          intUserId = data.data.user_id;
        }
      }

      if (!intUserId) {
        setMessage('Could not identify your account. Please try again.');
        setLoading(false);
        return;
      }

      await deleteUserById(intUserId);
      localStorage.clear();
      setSuccess(true);
      setMessage('Account deleted successfully. Redirecting...');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setMessage('Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return (
      <div className="xi-public xi-result">
        <div className="xi-public__veil" aria-hidden="true" />
        <div className="xi-public__body xi-result__body">
          <div className="xi-enroll__brand">
            <XiLogo size="sm" tagline />
          </div>

          <div className="xi-auth-card xi-result__card" role="status">
            <span className="xi-result__icon" aria-hidden="true">
              <LuLock />
            </span>
            <h1>{t('Deactivate Account')}</h1>
            <p className="xi-muted">
              {t('You need to be logged in to delete your account.')}
            </p>
            <div className="xi-result__actions">
              <button
                type="button"
                className="xi-btn xi-btn--primary"
                onClick={() => navigate('/')}
              >
                {t('Go to Login')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="xi-public xi-result">
      <div className="xi-public__veil" aria-hidden="true" />
      <div className="xi-public__body xi-result__body">
        <div className="xi-enroll__brand">
          <XiLogo size="sm" tagline />
        </div>

        <div className="xi-auth-card xi-result__card xi-result__card--start" role="region" aria-labelledby="delete-title">
          <span className="xi-result__icon is-bad" aria-hidden="true">
            <LuTriangleAlert />
          </span>
          <h1 id="delete-title">{t('Deactivate Account')}</h1>
          <p className="xi-muted">
            {t('Are you sure you want to delete your account? This action cannot be undone.')}
          </p>

          <div className="xi-alert xi-alert--bad" role="note">
            <LuTriangleAlert aria-hidden="true" />
            <div className="xi-alert__body">
              <p className="xi-alert__title">
                {t('The following data will be permanently deleted:')}
              </p>
              <ul className="xi-result__list">
                <li>{t('All session data and voice recordings')}</li>
                <li>{t('All emotion analysis results')}</li>
                <li>{t('All session notes')}</li>
                <li>{t('Your profile information')}</li>
              </ul>
            </div>
          </div>

          {message && (
            <div
              className={`xi-alert ${success ? 'xi-alert--ok' : 'xi-alert--bad'}`}
              role={success ? 'status' : 'alert'}
            >
              {success ? <LuCircleCheck aria-hidden="true" /> : <LuTriangleAlert aria-hidden="true" />}
              <div className="xi-alert__body">
                <p className="xi-alert__text">{message}</p>
              </div>
            </div>
          )}

          {!success && (
            <div className="xi-result__actions">
              <button
                type="button"
                className="xi-btn xi-btn--danger xi-btn--lg"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? t('Deleting...') : t('Yes, Delete My Account')}
              </button>
              <button
                type="button"
                className="xi-btn xi-btn--lg"
                onClick={() => navigate('/')}
              >
                {t('Cancel')}
              </button>
            </div>
          )}

          <div className="xi-result__foot">
            <button
              type="button"
              className="xi-btn xi-btn--ghost"
              onClick={() => navigate('/')}
            >
              <LuArrowLeft aria-hidden="true" />
              {t('Back to Profile')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
